import type { FC, FormEvent } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Link, Separator, Spacer, Typography } from "@aviary";
import { EmbeddedVideo } from "@shared/components";
import { useAnalyticsUpdateProfileMutation } from "@shared/data/mutations/AnalyticsUpdateProfileMutation";
import {
  AnalyticsEvent,
  useAnalyticsLogger,
  useBreakpoints,
  useUnauthUrlFields,
} from "@shared/hooks";
import { useSearchParams } from "@shared/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/shared/locales/i18n";

import { PageOne } from "./PageOne";
import { isFormValid } from "./PageOne/helpers";
import { PageTwo } from "./PageTwo";
import type { SimplifySignUpWithSignUpPageProps } from "./types";

import * as styles from "./SimplifySignUp.styles";

// pass formData and setFormData to pages, allow fields to call setFormData

const SignUpPageSelector: FC<SimplifySignUpWithSignUpPageProps> = props => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [searchParams, setSearchParams] = useSearchParams();
  const [analyticsUpdateProfile] = useAnalyticsUpdateProfileMutation({});
  const unauthUrlFields = useUnauthUrlFields();
  const isPeerReferral =
    !props.isEmerson && !!props.additionalAttributes?.peerReferralDisplayAttributes;

  const step = searchParams.get("step");

  const isFirstStep = () => !step || step === "1";

  const isValidStep = () => {
    return isFirstStep() || step === "2";
  };

  useEffect(() => {
    if (!isValidStep() || (step === "2" && !isFormValid(props.formData))) {
      searchParams.set("step", "1");
      setSearchParams(searchParams);
    } else if (isFirstStep()) {
      props.setFormData(prevData => ({ ...prevData, tosTimestamp: "" }));
    }
  }, [step]);

  const logAnalyticsEvent = useAnalyticsLogger();

  const getPage = () => {
    if (step === "2") return <PageTwo {...props} />;

    return (
      <PageOne onClick={onClick} additionalAttributes={props.additionalAttributes} {...props} />
    );
  };

  const getNextPage = () => {
    if (step === "2") return "1";
    else return "2";
  };

  const onClick = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextStep = getNextPage();
    searchParams.set("step", nextStep);
    setSearchParams(searchParams);

    logAnalyticsEvent({
      eventName: AnalyticsEvent.STORE_SIGNUP_TO_SIGNUP_METHOD,
      payload: {
        ...unauthUrlFields,
        page_version: "simplify_sign_up",
        account_modality: props.formData.practitionerType.description,
      },
    });

    updateAnalyticsProfile();
  };

  const updateAnalyticsProfile = () => {
    if (Object.keys({ ...unauthUrlFields }).length !== 0) {
      analyticsUpdateProfile({
        variables: {
          input: {
            payload: { ...unauthUrlFields },
          },
        },
      });
    }
  };

  const renderSignInLink = () => {
    return (
      <Arrange justify="center" data-testid="form-sign-in-link">
        <Typography isMarginless>
          {t(l.common.HaveAcount)}{" "}
          <Link isColor="primary" to={authRoutes.login}>
            {t(l.common.SignIn)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const logImAPatientEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.STORE_SIGNUP_IM_A_PATIENT,
      payload: { ...unauthUrlFields },
    });
  };

  const renderPatientLinkOrVideo = () => {
    if (isPeerReferral) {
      return (
        <>
          {tablet.lessThan && <Spacer height="oneHalf" />}
          <EmbeddedVideo
            src="https://www.youtube.com/embed/RH9goQv6qmE"
            title={l.practitionerSignUp.peerReferral.demoVideoModalTitle}
          />
        </>
      );
    }

    return (
      <Arrange css={styles.patientLink} justify="center" data-testid="patient-sign-up-link">
        <Typography isMarginless>
          {t(l.common.IAmAPatient)}{" "}
          <Link isColor="primary" to={authRoutes.patient_sign_up} onClick={logImAPatientEvent}>
            {t(l.common.FindInvite)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const renderPatientLinkOrVideoForPractitioner = () =>
    !props.isEmerson && (
      <>
        {!tablet.lessThan && isFirstStep() && renderSeparator()}
        {isFirstStep() && renderPatientLinkOrVideo()}
      </>
    );

  const renderSeparator = () => {
    if (isPeerReferral) return null;

    return (
      <div css={styles.dividerBox}>
        <Separator />
      </div>
    );
  };

  return (
    <>
      {getPage()}
      {tablet.lessThan && renderSignInLink()}
      {renderPatientLinkOrVideoForPractitioner()}
    </>
  );
};

export { SignUpPageSelector };
